import { Box, Paper, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { List } from "@mui/icons-material";
import {
  Acessos,
  ContatosExternosPage,
  ContatosInternosPage,
  DescricaoBarragem,
  DescricaoEmpreendimento,
  IdentificacaoEmpreendimentoEmpreendedor,
  ObjSumario,
  RecursosMateriaisLogisticos,
  Sumario
} from "../../components";
import '@mdxeditor/editor/style.css';
import { useBarragemContext, useUserContext } from "../../context";
import { ReactFlowProvider } from "@xyflow/react";
import { FlowProvider } from "../../context/FluxogramaContext";
import { Diagrama } from "../Flow";

export const SectionI: React.FC = () => {
  const theme = useTheme();
  const { barragem } = useBarragemContext();
  const user = useUserContext();

  const [showSumario, setShowSumario] = useState(true);
  const [screen, setScreen] = useState<JSX.Element>(<DescricaoEmpreendimento />);
  const [itemsSumario, setItemsSumario] = useState<ObjSumario[]>([]);

  useEffect(() => {
    if (!barragem || "error" in barragem) return;
    if (!user || "error" in user) return;

    setItemsSumario([
      {
        title: "Identificação e Contatos",
        type: "title",
        subitens: [
          {
            title: "Identificação do Empreendimento e Empreendedor",
            type: "item",
            screen: <IdentificacaoEmpreendimentoEmpreendedor />,
          },
          {
            title: "Lista de Contatos Emergenciais Internos",
            type: "item",
            screen: <ContatosInternosPage idBarragem={barragem.id} />,
          },
          {
            title: "Lista de Contatos Emergenciais Externos",
            type: "item",
            screen: <ContatosExternosPage idBarragem={barragem.id} isAdmin={user.admin ?? false} />,
          },
        ],
      },
      {
        title: "Descriçao Geral da Barragem",
        type: "title",
        subitens: [
          {
            title: "Descrição do Empreendimento",
            type: "item",
            screen: <DescricaoEmpreendimento />,
          },
          {
            title: "Descrição da Barragem",
            type: "item",
            screen: <DescricaoBarragem />,
          },
          {
            title: "Descrição das Rotas Alternativas",
            type: "item",
            screen: <Acessos />,
          },
        ],
      },
      {
        title: "Recursos Materiais e Logísticos Disponíveis",
        type: "title",
        screen: <RecursosMateriaisLogisticos />,
      },
      {
        title: "Responsabilidades das Equipes Internas",
        type: "title",
        screen: <ContatosInternosPage idBarragem={barragem.id} />,
      },
      {
        title: "Fluxogramas",
        type: "title",
        subitens: barragem.fluxogramas && !("error" in barragem.fluxogramas)
          ? [

            {
              title: "Nível de Alerta",
              type: "item",
              screen: flowScreenRender(barragem.fluxogramas.NV0),
            },
            {
              title: "Nível 1",
              type: "item",
              screen: flowScreenRender(barragem.fluxogramas.NV1),
            },
            {
              title: "Nível 2",
              type: "item",
              screen: flowScreenRender(barragem.fluxogramas.NV2),
            },
            {
              title: "Nível 3",
              type: "item",
              screen: flowScreenRender(barragem.fluxogramas.NV3),
            },
          ]
          :
          [
            {
              title: "Fluxogramas não disponíveis",
              type: "item",
              screen: (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="h6">
                    Os fluxogramas não estão disponíveis para esta barragem
                  </Typography>
                </Box>
              ),
            },
          ],
      }
    ]);

  }, [barragem, user]);


  return <Box sx={{
    py: "1em",
    pr: "1em",
    gap: "0.5em",
    width: `100vw`,
    height: `calc(100vh - 2.8em)`,
    display: "flex",
    flexDirection: "row",
    pl: showSumario ? "1em" : 0,
    overflow: "clip",
  }}>

    {/* Sumário */}
    {showSumario &&
      <Box sx={{
        width: "25%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
      }}>
        <Sumario
          items={itemsSumario}
          onSelect={(item) => item.screen && setScreen(item.screen)}
          onClose={() => setShowSumario(false)}
        />
      </Box>
    }

    {/* Botão para abrir o sumário, quando fechado */}
    {!showSumario &&
      <div
        onClick={() => setShowSumario(true)}
        style={{
          padding: "5px",
          position: 'relative',
          display: "flex",
          height: "46px",
          width: "46px",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.background.paper,
          border: "1px solid black",
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
          boxShadow: "10px",
          top: 5,
          left: 0,
          zIndex: 10000,
        }}
      >
        <List />
      </div>
    }

    {/* Editor */}
    <Paper elevation={0} sx={{
      flex: 1,
      width: showSumario ? "75%" : "100%",
      height: "calc(100vh - 2.8em - 2em)",
      textAlign: "justify",
      p: "15px",
    }}>
      {screen}
    </Paper>
  </Box >;
};

const flowScreenRender = (flowId: number | null) => {
  if (flowId) {
    return (
      <ReactFlowProvider>
        <FlowProvider flowId={flowId}>
          <Diagrama justView={true} />
        </FlowProvider>
      </ReactFlowProvider>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Typography variant="h6">Este fluxograma ainda não foi elaborado.</Typography>
    </Box>
  );
};
